// Overview: the scheduling dataset, the procedures, the theatre booking, and
// consent.
//
// Booking lives here rather than on the create form because the conflict
// answers — "OT-1 is already booked 09:00 to 11:00", "this patient already has
// an overlapping booking" — only mean something against a case that exists.
// The HARD refusals and the SOFT warnings both come from the server; this
// screen shows them and never second-guesses either.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import {
  IPD_COLORS,
  IpdButton,
  IpdConfirmDialog,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import { printConsentForm, printAnesthesiaConsentForm } from "../../utils/otPdf";
import {
  addProcedure,
  bookTheatre,
  cancelBooking,
  fetchSetup,
  openConsentFile,
  recordConsent,
  removeProcedure,
  rescheduleBooking,
  updateProcedure,
} from "../../api/otApi";
import { Field, FieldGrid, OtSelect, errorMessage, report } from "../common";
import { CLASSIFICATION_OT_SURGERIES, CONSENT_STATUSES } from "../../constants";
import ChargeServiceAutocomplete from "../../../ot-charge-capture/ChargeServiceAutocomplete";

export default function OverviewPanel({ otCase, onChanged }) {
  const [setup, setSetup] = useState(null);
  const [booking, setBooking] = useState(null); // the open booking dialog
  const [consentOpen, setConsentOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    fetchSetup().then(setSetup).catch(() => setSetup(null));
  }, []);

  const activeBooking = (otCase.bookings || []).find((b) => b.status === "ACTIVE");
  const nameOf = useCallback(
    (list, id) => (setup?.[list] || []).find((x) => x.id === id)?.name || null,
    [setup]
  );

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <IpdSectionCard title="Surgical Case Parameters & Booking Profile">
        <FieldGrid columns={3}>
          {/* Read-only: it comes from the doctor's request and OT never
              asks for it. The source is part of the value because a
              snapshot with no attribution is worse than none. */}
          <Field
            label="Diagnosis / indication"
            value={
              otCase.diagnosis
                ? `${otCase.diagnosis}${
                    otCase.diagnosis_source
                      ? ` — from ${otCase.diagnosis_source}${
                          otCase.diagnosis_recorded_at
                            ? `, ${formatIstDateTime(otCase.diagnosis_recorded_at)}`
                            : ""
                        }`
                      : ""
                  }`
                : null
            }
            span={2}
          />
          <Field label="Ward" value={otCase.ward_name} />
          <Field label="Anaesthesia" value={nameOf("anaesthesia_types", otCase.anaesthesia_type_id)} />
          <Field label="Position" value={nameOf("surgery_positions", otCase.surgery_position_id)} />
          <Field label="Incision" value={nameOf("incision_types", otCase.incision_type_id)} />
          <Field
            label="Estimated duration"
            value={otCase.estimated_duration_min ? `${otCase.estimated_duration_min} min` : null}
          />
          <Field label="PAC required" value={otCase.pac_required ? "Yes" : "No"} />
          <Field label="Remarks for the list" value={otCase.list_remarks} />
          <Field label="Special instructions" value={otCase.special_instructions} span={3} />
        </FieldGrid>
      </IpdSectionCard>

      <ProceduresCard otCase={otCase} onChanged={onChanged} />

      <IpdSectionCard
        title="Operating Theatre Reservation"
        subtitle="Allocated operating room slot and surgical session timing."
        actions={
          <Button
            size="small"
            variant="contained"
            onClick={() =>
              setBooking(
                activeBooking
                  ? {
                      mode: "reschedule",
                      id: activeBooking.id,
                      operating_room_id: activeBooking.operating_room_id,
                      start_datetime: toLocalInput(activeBooking.start_datetime),
                      end_datetime: toLocalInput(activeBooking.end_datetime),
                      reason: "",
                    }
                  : { mode: "book", operating_room_id: "", start_datetime: "", end_datetime: "" }
              )
            }
          >
            {activeBooking ? "Reschedule Slot" : "Allocate Operating Theatre"}
          </Button>
        }
      >
        {activeBooking ? (
          <>
            <FieldGrid columns={4}>
              <Field label="Theatre" value={activeBooking.operating_room_name} />
              <Field label="Start" value={formatIstDateTime(activeBooking.start_datetime)} />
              <Field label="End" value={formatIstDateTime(activeBooking.end_datetime)} />
              <Field label="Rescheduling Count" value={`${otCase.reschedule_count} time(s)`} />
            </FieldGrid>
            <Box sx={{ mt: 2 }}>
              <Button size="small" color="error" onClick={() => setCancelling(true)}>
                Release Theatre Reservation
              </Button>
              <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
                Cancellation releases the allocated operating room slot. The underlying surgical case remains on the waiting list until explicitly cancelled or rescheduled.
              </Typography>
            </Box>
          </>
        ) : (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            No operating theatre currently allocated. Booking an active slot confirms the theatre reservation.
          </Typography>
        )}

        {(otCase.booking_history || []).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>
              Booking history
            </Typography>
            {otCase.booking_history.map((h) => (
              <Typography
                key={h.id}
                sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 0.5 }}
              >
                {formatIstDateTime(h.created_at)} — <strong>{h.action}</strong>
                {h.new_start_datetime
                  ? ` to ${formatIstDateTime(h.new_start_datetime)}`
                  : ""}
                {h.reason ? ` — ${h.reason}` : ""}
                {h.actor_name ? ` (${h.actor_name})` : ""}
              </Typography>
            ))}
          </Box>
        )}
      </IpdSectionCard>

      <IpdSectionCard
        title="Informed Surgical & Anesthetic Consent"
        subtitle="Statutory informed consent documentation verified prior to patient transfer to theatre."
        actions={
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* The form, with the patient and the operation already on it. The
                one thing that must never be wrong on a consent — which patient,
                which operation — should not be handwritten at the bedside. */}
            <Button
              size="small"
              startIcon={<PrintIcon />}
              onClick={() =>
                printConsentForm(
                  otCase,
                  (otCase.procedures || []).find((p) => p.is_primary)?.procedure_name,
                )
              }
            >
              Print Surgical Consent
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() =>
                printAnesthesiaConsentForm(
                  otCase,
                  otCase.pac_details || otCase.pac || {},
                )
              }
            >
              Print Anesthesia Consent
            </Button>
            <Button size="small" variant="contained" onClick={() => setConsentOpen(true)}>
              Record consent
            </Button>
          </Box>
        }
      >
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: otCase.consent?.satisfied ? IPD_COLORS.success.bg : IPD_COLORS.warning.bg,
            color: otCase.consent?.satisfied ? IPD_COLORS.success.text : IPD_COLORS.warning.text,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {otCase.consent?.satisfied
            ? `Consent ${String(otCase.consent.status).toLowerCase()} — the gate is open`
            : `Consent is ${String(otCase.consent?.status || "not recorded").toLowerCase()}`}

          {(otCase.consents || []).length === 0 ? (
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
              Nothing recorded yet.
            </Typography>
          ) : (
            otCase.consents.map((c) => (
              <Box
                key={c.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  py: 1,
                  borderBottom: `1px solid ${IPD_COLORS.border}`,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                    {c.status}
                    {c.template_name ? ` — ${c.template_name}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {c.signed_at ? `Signed ${formatIstDateTime(c.signed_at)}` : "Not signed"}
                    {c.actor_name ? ` · recorded by ${c.actor_name}` : ""}
                    {c.notes ? ` · ${c.notes}` : ""}
                  </Typography>
                </Box>
                {c.has_file && (
                  <Button
                    size="small"
                    onClick={async () => {
                      try {
                        const link = await openConsentFile(c.id);
                        window.open(link.url, "_blank", "noopener");
                      } catch (err) {
                        report({ ok: false, message: errorMessage(err, "Could not open the scan") });
                      }
                    }}
                  >
                    Open scan
                  </Button>
                )}
              </Box>
            ))
          )}
        </Box>
      </IpdSectionCard>

      <BookingDialog
        state={booking}
        rooms={setup?.operating_rooms || []}
        onClose={() => setBooking(null)}
        onDone={() => {
          setBooking(null);
          onChanged();
        }}
        caseId={otCase.id}
      />
      <IpdConfirmDialog
        open={cancelling}
        destructive
        requireReason
        reasonLabel="Why is the theatre being given up?"
        reasonValue={cancelReason}
        onReasonChange={setCancelReason}
        title="Cancel this booking"
        description="The theatre time is released. The case itself stays open — cancelling the case is a separate decision."
        confirmLabel="Cancel booking"
        cancelLabel="Keep it"
        onConfirm={async () => {
          if (report(await cancelBooking(activeBooking.id, cancelReason.trim()))) {
            setCancelling(false);
            setCancelReason("");
            onChanged();
          }
        }}
        onCancel={() => {
          setCancelling(false);
          setCancelReason("");
        }}
      />
      <ConsentDialog
        open={consentOpen}
        caseId={otCase.id}
        onClose={() => setConsentOpen(false)}
        onDone={() => {
          setConsentOpen(false);
          onChanged();
        }}
      />
    </Box>
  );
}

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in local time. */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function ProceduresCard({ otCase, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ medical_service_id: "", procedure_name: "", is_primary: false });
  // The chosen master row, kept whole so the picker can show it back.
  const [service, setService] = useState(null);

  const procedures = otCase.procedures || [];

  return (
    <IpdSectionCard
      title="Procedures"
      subtitle="One primary procedure, plus any combined procedures."
      actions={
        <Button size="small" variant="contained" onClick={() => setAdding(true)}>
          Add procedure
        </Button>
      }
    >
      {procedures.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No procedure recorded yet.
        </Typography>
      ) : (
        procedures.map((p) => (
          <Box
            key={p.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1,
              borderBottom: `1px solid ${IPD_COLORS.border}`,
            }}
          >
            <IconButton
              size="small"
              title={p.is_primary ? "Primary procedure" : "Make primary"}
              onClick={async () => {
                if (p.is_primary) return;
                if (report(await updateProcedure(otCase.id, p.id, { is_primary: true })))
                  onChanged();
              }}
            >
              {p.is_primary ? (
                <StarIcon sx={{ fontSize: 18, color: "#F9A825" }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: p.is_primary ? 700 : 500 }}>
                {p.procedure_name || `Service #${p.medical_service_id}`}
              </Typography>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                {p.procedure_code || "no code"}
                {p.performed ? " · performed" : " · planned"}
                {p.notes ? ` · ${p.notes}` : ""}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={async () => {
                if (report(await removeProcedure(otCase.id, p.id))) onChanged();
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
        </Box>
        ))
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a procedure</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
            Procedures come from the EMR&apos;s medical-service master, so surgeries reconcile with
            the rest of the system.
          </Typography>
          {/* This dialog told the reader that procedures come from the
              medical-service master and then asked them to type its numeric
              id from memory, which is why procedures came out as
              "Service #123" with nothing behind them. Same picker the charge
              screen uses, searching the same master. */}
          <ChargeServiceAutocomplete
            label="Procedure"
            required
            placeholder="Type to search the surgery catalogue…"
            classificationName={CLASSIFICATION_OT_SURGERIES}
            value={service}
            onChange={(item) => {
              setService(item);
              setForm((f) => ({
                ...f,
                medical_service_id: item ? item.medical_service_id || item.id : "",
                procedure_name: item ? item.name : "",
              }));
            }}
          />
          <Box sx={{ mt: 2 }}>
            <IpdFormField label="Name as it should read on the record">
              <IpdTextField
                fullWidth
                value={form.procedure_name}
                onChange={(e) => setForm((f) => ({ ...f, procedure_name: e.target.value }))}
              />
            </IpdFormField>
          </Box>
          <Box sx={{ mt: 2 }}>
            <IpdFormField label="Primary?">
              <OtSelect
                value={form.is_primary ? "yes" : "no"}
                onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.value === "yes" }))}
                options={[
                  { value: "no", label: "Additional procedure" },
                  { value: "yes", label: "Primary procedure" },
                ]}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.medical_service_id}
            onClick={async () => {
              if (report(await addProcedure(otCase.id, form))) {
                setAdding(false);
                setForm({ medical_service_id: "", procedure_name: "", is_primary: false });
                setService(null);
                onChanged();
              }
            }}
          >
            Add
          </IpdButton>
        </DialogActions>
      </Dialog>
    </IpdSectionCard>
  );
}

function BookingDialog({ state, rooms, caseId, onClose, onDone }) {
  const [form, setForm] = useState(state || {});
  useEffect(() => setForm(state || {}), [state]);

  if (!state) return null;
  const isReschedule = state.mode === "reschedule";

  const submit = async () => {
    const payload = {
      operating_room_id: form.operating_room_id,
      // A `datetime-local` value carries no offset. Converting through Date
      // gives the browser's own zone, which is the one the coordinator typed in.
      start_datetime: form.start_datetime ? new Date(form.start_datetime).toISOString() : "",
      end_datetime: form.end_datetime ? new Date(form.end_datetime).toISOString() : "",
    };
    const res = isReschedule
      ? await rescheduleBooking(state.id, { ...payload, reason: form.reason })
      : await bookTheatre(caseId, payload);
    if (report(res)) onDone();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isReschedule ? "Reschedule the booking" : "Book a theatre"}</DialogTitle>
      <DialogContent>
        <IpdFormField label="Theatre">
          <OtSelect
            placeholder="Choose a theatre"
            value={form.operating_room_id || ""}
            onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
            options={rooms
              .filter((r) => r.active)
              .map((r) => ({
                value: r.id,
                label: `${r.name}${r.open_time ? ` (${r.open_time}–${r.close_time})` : ""}`,
              }))}
          />
        </IpdFormField>
        <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <IpdFormField label="Start">
            <IpdTextField
              fullWidth
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.start_datetime || ""}
              onChange={(e) => setForm((f) => ({ ...f, start_datetime: e.target.value }))}
            />
          </IpdFormField>
          <IpdFormField label="End">
            <IpdTextField
              fullWidth
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.end_datetime || ""}
              onChange={(e) => setForm((f) => ({ ...f, end_datetime: e.target.value }))}
            />
          </IpdFormField>
        </Box>
        {isReschedule && (
          <Box sx={{ mt: 2 }}>
            <IpdFormField label="Reason for the change">
              <IpdTextField
                fullWidth
                value={form.reason || ""}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        )}
        <Typography sx={{ mt: 2, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          A clash with another booking on the same theatre, or with a room block, is refused. A
          clash with this patient&apos;s own other booking is only a warning.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton
          disabled={!form.operating_room_id || !form.start_datetime || !form.end_datetime}
          onClick={submit}
        >
          {isReschedule ? "Reschedule" : "Book"}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function ConsentDialog({ open, caseId, onClose, onDone }) {
  const [form, setForm] = useState({ status: "PENDING", template_name: "", notes: "" });
  const [file, setFile] = useState(null);

  const submit = async () => {
    const payload = { ...form };
    if (file) {
      payload.consent_form = file.data;
      payload.file_type = file.type;
    }
    if (report(await recordConsent(caseId, payload))) {
      setForm({ status: "PENDING", template_name: "", notes: "" });
      setFile(null);
      onDone();
    }
  };

  const readFile = (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    const reader = new FileReader();
    reader.onload = () => {
      const ext = (chosen.name.split(".").pop() || "").toLowerCase();
      setFile({ data: String(reader.result), type: ext });
    };
    reader.readAsDataURL(chosen);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Record consent</DialogTitle>
      <DialogContent>
        <IpdFormField label="Status">
          <OtSelect
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            options={CONSENT_STATUSES}
          />
        </IpdFormField>
        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Template used">
            <IpdTextField
              fullWidth
              value={form.template_name}
              onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
            />
          </IpdFormField>
        </Box>
        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Notes">
            <IpdTextField
              fullWidth
              multiline
              minRows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </IpdFormField>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12.5, mb: 0.5 }}>Signed scan (PDF, JPG or PNG)</Typography>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={readFile} />
          <Typography sx={{ mt: 0.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
            Optional — record &ldquo;pending&rdquo; when the form is handed over and attach the scan
            when it comes back signed.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton onClick={submit}>Save</IpdButton>
      </DialogActions>
    </Dialog>
  );
}
